import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { FIGTURES_FOUNDING_YEAR } from '@/lib/brandCopy';

const Preloader = ({ onComplete }: { onComplete: () => void }) => {
  const { lang } = useLanguage();
  const [phase, setPhase] = useState<'enter' | 'brand' | 'tagline' | 'exit' | 'done'>('enter');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hasVisited = sessionStorage.getItem('figtures_preloader_seen') === '1';

    if (prefersReducedMotion || hasVisited) {
      setPhase('done');
      onComplete();
      return;
    }

    sessionStorage.setItem('figtures_preloader_seen', '1');

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        const increment = prev < 50 ? 2.4 : prev < 85 ? 3.2 : 1.2;
        return Math.min(prev + increment, 100);
      });
    }, 35);

    const t1 = setTimeout(() => setPhase('brand'), 150);
    const t2 = setTimeout(() => setPhase('tagline'), 400);
    const t3 = setTimeout(() => setPhase('exit'), 550);
    const t4 = setTimeout(() => {
      setPhase('done');
      onComplete();
    }, 650);

    return () => {
      clearInterval(interval);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [onComplete]);

  return (
    <>
      <div data-preloader={phase === 'done' ? 'done' : 'active'} className="hidden" />
      <AnimatePresence>
        {phase !== 'done' && (
          <motion.div
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
            style={{ background: 'linear-gradient(145deg, #0a0a0a 0%, #111111 40%, #0d0d0d 100%)' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
          >
          {/* Animated grain texture */}
          <motion.div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
              backgroundSize: '128px 128px',
            }}
            animate={{ x: [0, -10, 5, 0], y: [0, 5, -10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          />

          {/* Radial glow behind logo */}
          <motion.div
            className="absolute w-[600px] h-[600px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)',
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.2, 1], opacity: [0, 0.8, 0.5] }}
            transition={{ duration: 2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Horizontal line accent - left (scaleX, not width, so this stays compositor-only) */}
          <motion.div
            className="absolute left-0 top-1/2 -translate-y-1/2 w-[35%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"
            style={{ transformOrigin: 'left' }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Horizontal line accent - right (scaleX, not width) */}
          <motion.div
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[35%] h-px bg-gradient-to-l from-transparent via-white/10 to-transparent"
            style={{ transformOrigin: 'right' }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Main brand block */}
          <div className="relative flex flex-col items-center z-10">
            {/* Pulsing dot */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                className="w-2.5 h-2.5 bg-white rounded-full shadow-[0_0_20px_4px_rgba(255,255,255,0.15)]"
                animate={{
                  scale: [1, 1.8, 1],
                  opacity: [0.4, 1, 0.4],
                }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              />
            </motion.div>

            {/* Brand name - glass opacity reveal like ForAI */}
            <div className="flex items-center overflow-hidden">
              {'figtures'.split('').map((char, i) => (
                <motion.span
                  key={i}
                  className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-[-0.04em] text-white"
                  initial={{
                    y: 80,
                    opacity: 0,
                  }}
                  animate={{
                    y: 0,
                    opacity: 1,
                  }}
                  transition={{
                    y: { duration: 0.9, delay: 0.5 + i * 0.07, ease: [0.22, 1, 0.36, 1] },
                    opacity: { duration: 0.9, delay: 0.5 + i * 0.07, ease: [0.22, 1, 0.36, 1] },
                  }}
                  style={{ display: 'inline-block' }}
                >
                  {char}
                </motion.span>
              ))}
            </div>

            {/* PRODUCT STUDIO - directly under logo */}
            <motion.p
              className="font-body text-[9px] sm:text-[10px] tracking-[0.5em] uppercase mt-3 text-white/25"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 1.8, ease: [0.22, 1, 0.36, 1] }}
            >
              PRODUCT STUDIO
            </motion.p>

            {/* Divider line */}
            <motion.div
              className="w-12 h-px bg-white/20 mt-6 mb-5"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 2.2, ease: [0.22, 1, 0.36, 1] }}
            />

            {/* Motto - glass reveal with proper timing */}
            <motion.p
              className="font-body text-[11px] sm:text-[13px] tracking-[0.08em] max-w-sm text-center leading-relaxed overflow-visible text-white/40"
              initial={{ opacity: 0, y: 20 }}
              animate={phase === 'tagline' || phase === 'exit' ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            >
              {lang === 'tr' ? 'İhtiyacınız neyse sadece anlatın' : 'Whatever you need, just tell us'}
            </motion.p>
          </div>

          {/* Progress section - bottom */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4">
            {/* Year / location badge */}
            <motion.div
              className="flex items-center gap-6 mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              <span className="font-body text-[8px] tracking-[0.3em] text-white/15 uppercase">EST. {FIGTURES_FOUNDING_YEAR}</span>
              <span className="w-1 h-1 bg-white/10 rounded-full" />
              <span className="font-body text-[8px] tracking-[0.3em] text-white/15 uppercase">ISTANBUL</span>
            </motion.div>

            {/* Progress percentage */}
            <motion.span
              className="font-body text-[11px] tracking-[0.2em] text-white/25 tabular-nums font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {Math.round(progress)}%
            </motion.span>

            {/* Progress bar — scaleX, not width, so each 35ms tick is compositor-only */}
            <div className="w-56 h-[1px] bg-white/[0.06] overflow-hidden rounded-full">
              <motion.div
                className="h-full w-full rounded-full"
                style={{
                  transformOrigin: 'left',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.4))',
                }}
                animate={{ scaleX: progress / 100 }}
                transition={{ duration: 0.1 }}
              />
            </div>
          </div>

          {/* Corner decorative elements */}
          <motion.div
            className="absolute top-8 left-8 flex flex-col gap-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <div className="w-6 h-px bg-white/10" />
            <div className="w-3 h-px bg-white/5" />
          </motion.div>

          <motion.div
            className="absolute top-8 right-8 flex flex-col items-end gap-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <div className="w-6 h-px bg-white/10" />
            <div className="w-3 h-px bg-white/5" />
          </motion.div>

          <motion.div
            className="absolute bottom-8 left-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <span className="font-body text-[7px] tracking-[0.4em] text-white/10 uppercase">
              figtures®
            </span>
          </motion.div>

          <motion.div
            className="absolute bottom-8 right-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <span className="font-body text-[7px] tracking-[0.4em] text-white/10 uppercase">
              v1.0
            </span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default Preloader;
